import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'

// ============================================================================
// Types
// ============================================================================

export type ResolvedTheme = 'light' | 'dark'
export type Theme = 'system' | ResolvedTheme

interface ThemeContextValue {
  /** User preference: system, light, or dark */
  theme: Theme
  /** Current effective theme applied to the document */
  resolvedTheme: ResolvedTheme
  /** Set theme preference */
  setTheme: (theme: Theme) => void
  /** Toggle between light and dark */
  toggleTheme: () => void
  /** Check if currently dark mode */
  isDark: boolean
  /** Check if currently following system appearance */
  isSystem: boolean
}

// ============================================================================
// Constants
// ============================================================================

const THEME_STORAGE_KEY = 'optioneer_theme'
const VALID_THEMES: Theme[] = ['system', 'light', 'dark']
const DEFAULT_THEME: Theme = 'system'
const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)'

// ============================================================================
// Helpers
// ============================================================================

/**
 * Apply theme class to document
 */
function applyThemeToDocument(theme: ResolvedTheme) {
  const root = document.documentElement

  // Remove both classes first
  root.classList.remove('light', 'dark')

  // Add the theme class
  root.classList.add(theme)

  // Also set color-scheme for native elements
  root.style.colorScheme = theme
}

/**
 * Get current system appearance.
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light'
  return window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light'
}

/**
 * Convert user preference to an effective document theme.
 */
function resolveThemePreference(theme: Theme, systemTheme: ResolvedTheme): ResolvedTheme {
  return theme === 'system' ? systemTheme : theme
}

/**
 * Get stored theme preference, if one exists.
 */
function getStoredThemePreference(): Theme | null {
  if (typeof window === 'undefined') return null

  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  if (stored && VALID_THEMES.includes(stored as Theme)) {
    return stored as Theme
  }

  return null
}

/**
 * Get initial theme preference from storage or default to system.
 */
function getInitialTheme(): Theme {
  const storedTheme = getStoredThemePreference()
  if (storedTheme) return storedTheme

  return DEFAULT_THEME
}

// ============================================================================
// Context
// ============================================================================

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

// ============================================================================
// Provider
// ============================================================================

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const userId = user?.id ?? null

  // Initialize theme from localStorage (prevents flash)
  const [theme, setThemeState] = useState<Theme>(() => getInitialTheme())
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme())
  const [hasLoadedFromDB, setHasLoadedFromDB] = useState(false)

  const resolvedTheme = resolveThemePreference(theme, systemTheme)

  // Apply initial theme immediately to prevent flash
  useEffect(() => {
    const initialTheme = resolveThemePreference(getInitialTheme(), getSystemTheme())
    applyThemeToDocument(initialTheme)
  }, [])

  // Apply theme to document when theme changes
  useEffect(() => {
    applyThemeToDocument(resolvedTheme)
  }, [resolvedTheme])

  // Keep system preference live when the OS appearance changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return

    const mediaQuery = window.matchMedia(SYSTEM_DARK_QUERY)
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light')
    }

    setSystemTheme(mediaQuery.matches ? 'dark' : 'light')
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  // Sync theme with database when user logs in
  // Priority: keep an explicit local choice, but honor DB preference on fresh devices.
  useEffect(() => {
    if (!userId || hasLoadedFromDB) return

    const syncThemeWithDB = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('theme_preference')
          .eq('id', userId)
          .limit(1)

        if (error) {
          console.warn('Failed to load theme from database')
          setHasLoadedFromDB(true)
          return
        }

        const dbTheme = data?.[0]?.theme_preference
        const storedTheme = getStoredThemePreference()
        const currentPreference = storedTheme || getInitialTheme()

        if (!dbTheme || !VALID_THEMES.includes(dbTheme as Theme)) {
          // No valid theme in DB - save current localStorage/default preference to DB
          await supabase
            .from('profiles')
            .update({ theme_preference: currentPreference })
            .eq('id', userId)
        } else if (!storedTheme) {
          // Fresh local device: keep the user's existing DB preference.
          const validDbTheme = dbTheme as Theme
          setThemeState(validDbTheme)
          localStorage.setItem(THEME_STORAGE_KEY, validDbTheme)
        } else if (dbTheme !== currentPreference) {
          // Local explicit preference wins over DB so recent local changes persist.
          await supabase
            .from('profiles')
            .update({ theme_preference: currentPreference })
            .eq('id', userId)
        }
        // If they match, do nothing - everything is in sync
      } catch {
        console.warn('Failed to sync theme with database')
      } finally {
        setHasLoadedFromDB(true)
      }
    }

    void syncThemeWithDB()
  }, [userId, hasLoadedFromDB])

  // Reset hasLoadedFromDB when user changes (logout/login different user)
  useEffect(() => {
    if (!userId) {
      setHasLoadedFromDB(false)
    }
  }, [userId])

  // Set theme with persistence
  const setTheme = useCallback(
    async (newTheme: Theme) => {
      // Update state immediately
      setThemeState(newTheme)

      // Persist to localStorage
      localStorage.setItem(THEME_STORAGE_KEY, newTheme)

      // Sync to database if user is logged in
      if (userId) {
        try {
          await supabase.from('profiles').update({ theme_preference: newTheme }).eq('id', userId)
        } catch {
          console.warn('Failed to sync theme to database')
        }
      }
    },
    [userId]
  )

  // Toggle theme helper
  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'light' ? 'dark' : 'light')
  }, [resolvedTheme, setTheme])

  // Context value
  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      resolvedTheme,
      setTheme,
      toggleTheme,
      isDark: resolvedTheme === 'dark',
      isSystem: theme === 'system',
    }),
    [theme, resolvedTheme, setTheme, toggleTheme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access theme context
 * @throws Error if used outside of ThemeProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

/**
 * Safe variant for contexts where ThemeProvider might not be mounted
 * (for example isolated export render roots).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalTheme(): ThemeContextValue | undefined {
  return useContext(ThemeContext)
}
